import React from 'react';
import {
  MIN_PERCENTAGE_VALUE,
  MAX_PERCENTAGE_VALUE,
  MIN_RANGE_VALUE,
  MAX_RANGE_VALUE,
  PERCENTAGE_START_POINT,
} from '../constants';
import { Box, FieldSet, Input, NumberInput, Slider } from '@wix/design-system';

type Mode = 'percentage' | 'range';

class SpacingControlBehavior {
  private mode: Mode;

  constructor({ mode }: { mode: Mode }) {
    this.mode = mode;
  }

  getMinValue() {
    return this.mode === 'percentage' ? MIN_PERCENTAGE_VALUE : MIN_RANGE_VALUE;
  }

  getMaxValue(controlValue: number) {
    return this.mode === 'percentage'
      ? MAX_PERCENTAGE_VALUE
      : controlValue < MAX_RANGE_VALUE
      ? MAX_RANGE_VALUE
      : controlValue;
  }

  valueFormat(value: number) {
    return this.mode === 'percentage' ? `${value}%` : `${value}px`;
  }

  getStartPoint() {
    return this.mode === 'percentage' ? PERCENTAGE_START_POINT : undefined;
  }
}

type SpacingControlProps = {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  percentageMode: boolean;
  dataHook?: string;
};

export const SpacingControl: React.FC<SpacingControlProps> = ({
  onChange,
  value,
  percentageMode,
  label,
  dataHook,
}) => {
  const behavior = new SpacingControlBehavior({
    mode: percentageMode ? 'percentage' : 'range',
  });

  const inputValue =
    typeof value === 'number' ? value : parseInt(value, 10) || 0;

  const minValue = behavior.getMinValue();
  const maxValue = behavior.getMaxValue(inputValue);

  return (
    <FieldSet
      legend={label}
      dataHook={dataHook}
      columns="auto 72px"
      alignment="center"
      gap="medium"
    >
      <Box direction="horizontal" align="center">
        <Slider
          displayMarks={false}
          min={minValue}
          max={maxValue}
          displayTooltip={percentageMode}
          startPoint={behavior.getStartPoint()}
          value={inputValue}
          formatValue={behavior.valueFormat.bind(behavior)}
          onChange={(v) =>
            onChange(percentageMode ? v?.toString() || '' : v + 'px')
          }
        />
      </Box>

      <NumberInput
        hideStepper
        min={minValue}
        max={maxValue}
        value={inputValue}
        suffix={
          percentageMode ? (
            <Input.Affix>%</Input.Affix>
          ) : (
            <Input.Affix>px</Input.Affix>
          )
        }
        onChange={(v) =>
          onChange(percentageMode ? v?.toString() || '' : v + 'px')
        }
      />
    </FieldSet>
  );
};
